import { useSelector, useDispatch } from 'react-redux';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { RootState } from '@store/reducers';
import { 
    setServiceRuleConfig,
    setServiceRuleLoading,
    setServiceRuleError,
    updateServiceRuleConfig,
    clearServiceRuleError,
    clearServiceRuleData
} from '@store/actions/serviceRuleMasterActions';
import { getData, postDataJSON } from '@services/CommonApiService';
import endpoints from '@config/endpoints';
import { errorLogger } from '@utils/applicationLogUtils';

// Hook to load and use a specific service rule config
export const useServiceRuleMaster = (configName: string) => {
    const dispatch = useDispatch();
    const configData = useSelector((state: RootState) => 
        state.serviceRuleMaster.configs[configName]
    );
    const loadingInitiated = useRef(false);
    const hasMounted = useRef(false);

    useEffect(() => {
        if (!hasMounted.current) {
            hasMounted.current = true;

            const hasData = configData?.data;
            const isCurrentlyLoading = loadingInitiated.current;

            if (!hasData && !isCurrentlyLoading) {
                loadingInitiated.current = true;

                dispatch(setServiceRuleLoading(configName, true));
                dispatch(clearServiceRuleError(configName));

                getData(endpoints.getServiceRuleMaster(configName))
                    .then(response => {
                        const rawData = (response as any).data?.data || (response as any).data || response;
                        
                        const transformedData: any = {};
                        if (Array.isArray(rawData)) {
                            rawData.forEach((rule: any) => {
                                const key = `${rule.service_code}_${rule.hrp_code}`;
                                const value = rule.parameter_min_value !== null && rule.parameter_min_value !== undefined 
                                    ? rule.parameter_min_value 
                                    : rule.parameter_max_value;
                                transformedData[key] = {
                                    ...rule,
                                    value: value
                                };
                            });
                        } else {
                            Object.assign(transformedData, rawData);
                        }
                        
                        dispatch(setServiceRuleConfig(configName, transformedData));
                    })
                    .catch(error => {
                        errorLogger(error, 'Error fetching service rule config for ' + configName);
                    })
                    .finally(() => {
                        loadingInitiated.current = false;
                    });
            }
        }
    }, []);

    const config = useMemo(() => configData?.data || null, [configData?.data]);
    const loading = useMemo(() => configData?.loading || false, [configData?.loading]);
    const error = useMemo(() => configData?.error || null, [configData?.error]);
    const lastUpdated = useMemo(() => configData?.lastUpdated || null, [configData?.lastUpdated]);

    const updateConfig = useCallback(
        async (updates: any) => {
            dispatch(setServiceRuleLoading(configName, true));
            dispatch(clearServiceRuleError(configName));

            try {
                const response = await postDataJSON(endpoints.updateServiceRuleMaster(configName), updates);
                const serviceRuleData = (response as any).data?.data || (response as any).data || response;
                dispatch(setServiceRuleConfig(configName, serviceRuleData));
                return true;
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : 'Unknown error';
                dispatch(setServiceRuleError(configName, errorMessage));
                errorLogger(error, 'Error updating service rule config for ' + configName);
                return false;
            }
        },
        [configName, dispatch]
    );

    const updateLocalConfig = useCallback(
        (updates: any) => {
            dispatch(updateServiceRuleConfig(configName, updates));
        },
        [configName, dispatch]
    );

    const reload = useCallback(() => {        
        loadingInitiated.current = false;
        hasMounted.current = false;
                
        dispatch(setServiceRuleLoading(configName, true));
        dispatch(clearServiceRuleError(configName));

        loadingInitiated.current = true;
        getData(endpoints.getServiceRuleMaster(configName))
            .then(response => {
                const rawData = (response as any).data?.data || (response as any).data || response;
                
                const transformedData: any = {};
                if (Array.isArray(rawData)) {
                    rawData.forEach((rule: any) => {
                        const key = `${rule.service_code}_${rule.hrp_code}`;
                        const value = rule.parameter_min_value !== null && rule.parameter_min_value !== undefined 
                            ? rule.parameter_min_value 
                            : rule.parameter_max_value;
                        transformedData[key] = {
                            ...rule,
                            value: value
                        };
                    });
                } else {
                    Object.assign(transformedData, rawData);
                }
                
                dispatch(setServiceRuleConfig(configName, transformedData));
                hasMounted.current = true;
            })
            .catch(error => {
                errorLogger(error, 'Error fetching service rule config for ' + configName);
            })
            .finally(() => {
                loadingInitiated.current = false;
            });
    }, [configName, dispatch]);

    const clearError = useCallback(() => {
        dispatch(clearServiceRuleError(configName));
    }, [configName, dispatch]);

    const forceRetry = useCallback(() => {        
        // Reset all refs
        loadingInitiated.current = false;
        hasMounted.current = false;
        
        dispatch(clearServiceRuleData(configName));
        dispatch(clearServiceRuleError(configName));
                
        // Force trigger the useEffect logic
        setTimeout(() => {
            reload();
        }, 100);
    }, [configName, dispatch, reload]);

    const clearAllData = useCallback(() => {
        loadingInitiated.current = false;
        hasMounted.current = false;
        dispatch(clearServiceRuleData(configName));
        dispatch(clearServiceRuleError(configName));
    }, [configName, dispatch]);

    return {
        config,
        loading,
        error,
        lastUpdated,
        updateConfig,
        updateLocalConfig,
        reload,
        forceRetry,
        clearError,
        clearAllData
    };
};

// Hook to get a specific value from service rule config
export const useServiceRuleValue = (
    configName: string, 
    key: string, 
    defaultValue: any = null
) => {
    const { config } = useServiceRuleMaster(configName);
    
    return useMemo(() => {
        if (!config) return defaultValue;
        
        const keys = key.split('.');
        let value = config;
        
        for (const k of keys) {
            if (value && typeof value === 'object' && k in value) {
                value = value[k];
            } else {
                return defaultValue;
            }
        }
        
        return value;
    }, [config, key, defaultValue]);
};

// Hook to update service rule configuration
export const useServiceRuleUpdater = (configName: string) => {
    const dispatch = useDispatch();

    // Async function to save config updates
    const saveConfig = useCallback(
        async (updates: any) => {
            dispatch(setServiceRuleLoading(configName, true));
            dispatch(clearServiceRuleError(configName));

            try {
                const response = await postDataJSON(endpoints.updateServiceRuleMaster(configName), updates);
                dispatch(clearServiceRuleData(configName));
                return true;
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : 'Unknown error';
                dispatch(setServiceRuleError(configName, errorMessage));
                return false;
            }
        },
        [configName, dispatch]
    );

    const updateValue = useCallback(
        async (key: string, value: any) => {
            const keys = key.split('.');
            const updates = keys.reduceRight(
                (acc, k) => ({ [k]: acc }),
                value
            );
            
            return saveConfig(updates);
        },
        [saveConfig]
    );

    const updateMultiple = useCallback(
        async (updates: Record<string, any>) => {
            return saveConfig(updates);
        },
        [saveConfig]
    );

    const updateLocalValue = useCallback(
        (key: string, value: any) => {
            const keys = key.split('.');
            const updates = keys.reduceRight(
                (acc, k) => ({ [k]: acc }),
                value
            );
            dispatch(updateServiceRuleConfig(configName, updates));
        },
        [configName, dispatch]
    );

    const updateLocalMultiple = useCallback(
        (updates: Record<string, any>) => {
            dispatch(updateServiceRuleConfig(configName, updates));
        },
        [configName, dispatch]
    );

    return {
        updateValue,
        updateMultiple,
        updateLocalValue,
        updateLocalMultiple
    };
};